import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type Channel = { key: string; icon?: string; mark?: string; color: string };

const channels: Channel[] = [
  {
    key: "whatsapp",
    icon: "ri-whatsapp-fill",
    color: "bg-[#25D366] text-white",
  },
  { key: "zalo", mark: "Zalo", color: "bg-[#0068FF] text-white" },
  { key: "wechat", icon: "ri-wechat-fill", color: "bg-[#07C160] text-white" },
  {
    key: "callNow",
    icon: "ri-phone-line",
    color: "bg-foreground-950 text-background-50",
  },
];

const PHONE_DISPLAY = "+84 918 335 899";
const PHONE_TEL = "tel:+84918335899";

export default function QuickContact() {
  const [open, setOpen] = useState(false);
  const [qrChannel, setQrChannel] = useState<Channel | null>(null);
  const [callOpen, setCallOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!qrChannel && !callOpen) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setQrChannel(null);
        setCallOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [qrChannel, callOpen]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const openQr = (channel: Channel) => {
    setOpen(false);
    setQrChannel(channel);
  };

  const openCall = () => {
    setOpen(false);
    setCallOpen(true);
  };

  const closeCall = () => {
    setCallOpen(false);
    setCopied(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(PHONE_DISPLAY);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const appName = qrChannel ? t(`quick.${qrChannel.key}`) : "";

  return (
    <>
      <div className="fixed bottom-5 right-4 md:bottom-8 md:right-8 z-50 flex flex-col items-end gap-3">
        <ul
          className={`flex flex-col items-end gap-3 transition-all duration-300 ${open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-3 pointer-events-none"}`}
        >
          {channels.map((c) => (
            <li key={c.key} className="flex items-center gap-2">
              <span className="px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-[11px] tracking-widest uppercase text-foreground-800 whitespace-nowrap">
                {t(`quick.${c.key}`)}
              </span>
              {c.key === "callNow" ? (
                <button
                  type="button"
                  onClick={openCall}
                  aria-label={t(`quick.${c.key}`)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-xl whitespace-nowrap cursor-pointer hover:scale-110 transition-transform ${c.color}`}
                >
                  <i className={c.icon} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => openQr(c)}
                  aria-label={t(`quick.${c.key}`)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-xl whitespace-nowrap cursor-pointer hover:scale-110 transition-transform ${c.color}`}
                >
                  {c.mark ? (
                    <span className="text-[13px] font-extrabold tracking-tight leading-none">
                      {c.mark}
                    </span>
                  ) : (
                    <i className={c.icon} />
                  )}
                </button>
              )}
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? t("quick.close") : t("quick.open")}
          className="relative w-14 h-14 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 flex items-center justify-center text-2xl whitespace-nowrap cursor-pointer transition-colors"
        >
          {!open && (
            <span className="absolute inset-0 rounded-full bg-primary-500/50 animate-ping" />
          )}
          <i
            className={`${open ? "ri-close-line" : "ri-customer-service-2-line"} relative`}
          />
        </button>
      </div>

      {qrChannel && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-foreground-950/60"
          role="dialog"
          aria-modal="true"
          aria-label={t("quick.qrTitle", { app: appName })}
          onClick={() => setQrChannel(null)}
        >
          <div
            className="relative w-full max-w-sm rounded-lg bg-background-50 border border-background-200 p-6 md:p-8 text-center animate-[fadeUp_.25s_ease-out]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setQrChannel(null)}
              aria-label={t("quick.qrClose")}
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg" />
            </button>

            <div
              className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center ${qrChannel.color}`}
            >
              {qrChannel.mark ? (
                <span className="text-sm font-extrabold tracking-tight leading-none">
                  {qrChannel.mark}
                </span>
              ) : (
                <i className={`${qrChannel.icon} text-2xl`} />
              )}
            </div>

            <h3 className="mt-4 font-[family-name:var(--font-heading)] text-xl text-foreground-950">
              {t("quick.qrTitle", { app: appName })}
            </h3>
            <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
              {t("quick.qrDesc", { app: appName })}
            </p>

            <div className="mt-5 mx-auto w-full max-w-[288px] aspect-square rounded-lg overflow-hidden border border-background-200 bg-background-50">
              {qrChannel.key === "whatsapp" ? (
                <img
                  src="https://static.readdy.ai/image/0fa28fca5906ca14f9eda1f6634fcd3a/c06176005b437535405b4542dd5ed9b0.png"
                  alt={t("quick.qrTitle", { app: appName })}
                  className="w-full h-full object-contain p-1"
                />
              ) : qrChannel.key === "wechat" ? (
                <img
                  src="https://static.readdy.ai/image/0fa28fca5906ca14f9eda1f6634fcd3a/7cbebea4ee7db4445aebb2cf792bb59a.png"
                  alt={t("quick.qrTitle", { app: appName })}
                  className="w-full h-full object-contain p-1"
                />
              ) : qrChannel.key === "zalo" ? (
                <img
                  src="https://static.readdy.ai/image/0fa28fca5906ca14f9eda1f6634fcd3a/ec74bf543c38461248747049f1c055dd.png"
                  alt={t("quick.qrTitle", { app: appName })}
                  className="w-full h-full object-contain p-1"
                />
              ) : (
                <div className="w-full h-full border-2 border-dashed border-background-300 flex flex-col items-center justify-center gap-2 px-4">
                  <i className="ri-qr-code-line text-4xl text-foreground-500" />
                  <span className="text-[11px] tracking-widest uppercase text-foreground-600 text-center leading-relaxed">
                    {t("quick.qrPlaceholder", { app: appName })}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-6 text-left">
              <p className="text-[11px] tracking-widest uppercase text-foreground-600 mb-3">
                {t("quick.qrStepsTitle")}
              </p>
              <ol className="space-y-2.5">
                {[1, 2, 3].map((step) => (
                  <li key={step} className="flex items-start gap-3">
                    <span className="w-5 h-5 shrink-0 rounded-full bg-secondary-100 text-secondary-900 text-[11px] flex items-center justify-center mt-0.5">
                      {step}
                    </span>
                    <span className="text-sm text-foreground-800 leading-relaxed">
                      {t(`quick.qrStep${step}`, { app: appName })}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <button
              type="button"
              onClick={() => setQrChannel(null)}
              className="mt-6 w-full px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer transition-colors"
            >
              {t("quick.qrClose")}
            </button>
          </div>
        </div>
      )}

      {callOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-foreground-950/60"
          role="dialog"
          aria-modal="true"
          aria-label={t("quick.callTitle")}
          onClick={closeCall}
        >
          <div
            className="relative w-full max-w-sm rounded-lg bg-background-50 border border-background-200 p-6 md:p-8 text-center animate-[fadeUp_.25s_ease-out]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeCall}
              aria-label={t("quick.callClose")}
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg" />
            </button>

            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-foreground-950 text-background-50">
              <i className="ri-phone-line text-2xl" />
            </div>

            <h3 className="mt-4 font-[family-name:var(--font-heading)] text-xl text-foreground-950">
              {t("quick.callTitle")}
            </h3>
            <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
              {t("quick.callDesc")}
            </p>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-100 px-4 py-5">
              <p className="text-[11px] tracking-widest uppercase text-foreground-600">
                {t("quick.callLabel")}
              </p>
              <p className="mt-2 text-2xl md:text-3xl tracking-wide tabular-nums text-foreground-950">
                {PHONE_DISPLAY}
              </p>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <a
                href={PHONE_TEL}
                className="flex-1 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-phone-fill text-base" />
                {t("quick.callNow")}
              </a>
              <button
                type="button"
                onClick={handleCopy}
                className="flex-1 px-6 py-3 rounded-md bg-secondary-100 hover:bg-secondary-200 text-secondary-900 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer transition-colors flex items-center justify-center gap-2"
              >
                <i
                  className={
                    copied
                      ? "ri-check-line text-base"
                      : "ri-file-copy-line text-base"
                  }
                />
                {copied ? t("quick.callCopied") : t("quick.callCopy")}
              </button>
            </div>

            <button
              type="button"
              onClick={closeCall}
              className="mt-4 w-full px-6 py-3 rounded-md border border-background-300 text-foreground-800 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
            >
              {t("quick.callClose")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
